import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  Grid, IconButton, Paper, Snackbar, Stack, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, CardGiftcard as GiftIcon,
} from '@mui/icons-material';
import LoyaltyService from '../../services/loyalty.service';
import { useTranslation } from '../../hooks/useTranslation';
import { useAuth } from '../../contexts/AuthContext';
import { apiError, formatPoints } from './loyaltyUtils';
import LoyaltyImagePicker from './LoyaltyImagePicker';

const empty = { name: '', description: '', points_required: '', active: true, starts_at: '', ends_at: '' };

const toInput = (v) => (v ? new Date(v).toISOString().slice(0, 10) : '');

const RewardList = () => {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('loyalty.manage');

  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState({ open: false, uuid: null });
  const [form, setForm] = useState(empty);
  const [image, setImage] = useState({ file: null, url: null, removed: false, error: null });
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const show = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await LoyaltyService.getRewards();
      setRewards(res.data?.data || []);
    } catch (error) {
      show(apiError(error, t('loyalty.errorLoading')), 'error');
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(empty); setImage({ file: null, url: null, removed: false, error: null }); setDialog({ open: true, uuid: null }); };
  const openEdit = (r) => {
    setForm({ name: r.name, description: r.description || '', points_required: r.points_required, active: !!r.active, starts_at: toInput(r.starts_at), ends_at: toInput(r.ends_at) });
    setImage({ file: null, url: r.image_url || null, removed: false, error: null });
    setDialog({ open: true, uuid: r.uuid });
  };

  const save = async () => {
    if (!form.name || !(parseInt(form.points_required, 10) > 0)) {
      show(t('common.snackbar.pleaseFillAllRequiredFields'), 'error');
      return;
    }
    const payload = {
      ...form,
      points_required: parseInt(form.points_required, 10),
      starts_at: form.starts_at || null,
      ends_at: form.ends_at || null,
    };
    try {
      setSaving(true);
      let uuid = dialog.uuid;
      if (uuid) {
        await LoyaltyService.updateReward(uuid, payload);
      } else {
        const res = await LoyaltyService.createReward(payload);
        uuid = res.data?.data?.uuid;
      }
      // Photo is optional and uploaded separately once the reward exists.
      if (uuid && image.file) {
        await LoyaltyService.uploadRewardImage(uuid, image.file);
      } else if (uuid && image.removed && image.url) {
        await LoyaltyService.removeRewardImage(uuid);
      }
      setDialog({ open: false, uuid: null });
      show(t('loyalty.rewardSaved'));
      load();
    } catch (error) {
      show(apiError(error, t('loyalty.errorSaving')), 'error');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(t('loyalty.deleteRewardConfirm'))) return;
    try {
      const res = await LoyaltyService.deleteReward(r.uuid);
      show(res.data?.message || t('loyalty.rewardDeleted'));
      load();
    } catch (error) {
      show(apiError(error, t('loyalty.errorSaving')), 'error');
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Typography variant="h4" fontWeight="bold">{t('loyalty.rewards')}</Typography>
        {canManage && <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>{t('loyalty.newReward')}</Button>}
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('common.name')}</TableCell>
                <TableCell>{t('common.description')}</TableCell>
                <TableCell align="right">{t('loyalty.pointsRequired')}</TableCell>
                <TableCell>{t('loyalty.validity')}</TableCell>
                <TableCell align="right">{t('loyalty.redemptions')}</TableCell>
                <TableCell>{t('common.status')}</TableCell>
                {canManage && <TableCell align="right">{t('common.actions')}</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {rewards.length === 0 && <TableRow><TableCell colSpan={7} align="center">{t('loyalty.noRewards')}</TableCell></TableRow>}
              {rewards.map((r) => (
                <TableRow key={r.uuid} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar variant="rounded" src={r.image_url || undefined} sx={{ width: 44, height: 44, bgcolor: 'primary.light' }}>
                        <GiftIcon fontSize="small" />
                      </Avatar>
                      <Typography fontWeight="bold">{r.name}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>{r.description || '—'}</TableCell>
                  <TableCell align="right">{formatPoints(r.points_required)}</TableCell>
                  <TableCell>{r.starts_at || r.ends_at ? `${toInput(r.starts_at) || '…'} → ${toInput(r.ends_at) || '…'}` : '—'}</TableCell>
                  <TableCell align="right">{r.redemptions_count ?? 0}</TableCell>
                  <TableCell><Chip size="small" label={r.active ? t('common.active') : t('common.inactive')} color={r.active ? 'success' : 'default'} /></TableCell>
                  {canManage && (
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => openEdit(r)}><EditIcon /></IconButton>
                      <IconButton size="small" color="error" onClick={() => remove(r)}><DeleteIcon /></IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialog.open} onClose={() => setDialog({ open: false, uuid: null })} maxWidth="sm" fullWidth>
        <DialogTitle>{dialog.uuid ? t('loyalty.editReward') : t('loyalty.newReward')}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12 }}>
              <LoyaltyImagePicker value={image} onChange={setImage} disabled={saving} t={t} />
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <TextField fullWidth required label={t('common.name')} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth required type="number" label={t('loyalty.pointsRequired')} value={form.points_required} onChange={(e) => setForm({ ...form, points_required: e.target.value })} inputProps={{ min: 1 }} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth multiline rows={2} label={t('common.description')} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth type="date" label={t('loyalty.startsAt')} InputLabelProps={{ shrink: true }} value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth type="date" label={t('loyalty.endsAt')} InputLabelProps={{ shrink: true }} value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel control={<Switch checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />} label={t('common.active')} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog({ open: false, uuid: null })}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={save} disabled={saving}>{t('common.save')}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default RewardList;
